import { create } from 'zustand';
import { FullSource, SourceType } from '@/types/source';
import _ from 'lodash';
import { enrichSource, enrichSources } from '@/utils/source';

export type SelectedFolders = Map<string, readonly string[]>;

interface SourcesState {
    sources: FullSource[];
    sourcesMap: Map<string, FullSource>;
    selected: Set<string>;
    selectedFolders: SelectedFolders;
    excluded: Set<string>;
    all: boolean;
    addSource: (source: FullSource) => void;
    addSources: (sources: FullSource[]) => void;
    setSources: (sources: FullSource[]) => void;
    updateSource: (sourceId: string, updates: Partial<FullSource>) => void;
    removeSource: (sourceId: string) => void;
    getSourceById: (id: string) => FullSource | undefined;
    selectSource: (id: string) => void;
    deselectSource: (id: string) => void;
    toggleSource: (id: string) => void;
    selectAll: () => void;
    clearSelection: () => void;
    setSelectedSources: (
        sourceIds: Set<string>,
        folders?: SelectedFolders,
    ) => void;
    getSelectedSourceIds: () => string[];
}

const createSourcesMap = (sources: FullSource[]): Map<string, FullSource> => {
    return new Map(sources.map((s) => [s.id, s]));
};

export const pruneFoldersContaining = (
    folders: SelectedFolders,
    removedIds: Iterable<string>,
): SelectedFolders => {
    const removed = new Set(removedIds);
    const entries = Array.from(folders).filter(
        ([, memberIds]) => !memberIds.some((memberId) => removed.has(memberId)),
    );
    return entries.length === folders.size ? folders : new Map(entries);
};

export const splitSourceSelection = (
    selected: Set<string>,
    selectedFolders: SelectedFolders,
): { folderIds: string[]; looseSourceIds: string[] } => {
    const coveredByFolders = new Set(
        Array.from(selectedFolders.values()).flat(),
    );
    return {
        folderIds: Array.from(selectedFolders.keys()),
        looseSourceIds: Array.from(selected).filter(
            (id) => !coveredByFolders.has(id),
        ),
    };
};

export const useSourcesStore = create<SourcesState>((set, get) => ({
    sources: [],
    sourcesMap: new Map(),
    selected: new Set(),
    selectedFolders: new Map(),
    excluded: new Set(),
    all: false,
    addSource: (source) => {
        const { sources } = get();
        if (!sources.find((s) => s.id === source.id)) {
            const enrichedSource = enrichSource(source);
            const newSources = [...sources, enrichedSource];
            set({
                sources: newSources,
                sourcesMap: createSourcesMap(newSources),
            });
        }
    },
    addSources: (sources) => {
        const { sources: prevSources } = get();
        const enrichedSources = enrichSources(sources);
        const newSources = _.uniqBy([...prevSources, ...enrichedSources], 'id');
        set({
            sources: newSources,
            sourcesMap: createSourcesMap(newSources),
        });
    },
    setSources: (sources) => {
        const enrichedSources = enrichSources(sources);
        set({
            sources: enrichedSources,
            sourcesMap: createSourcesMap(enrichedSources),
        });
    },
    updateSource: (sourceId, updates) => {
        const { sourcesMap } = get();
        const existingSource = sourcesMap.get(sourceId);
        if (!existingSource) return;

        const merged = { ...existingSource, ...updates };
        const shouldRederiveDisplayName =
            'name' in updates && !('displayName' in updates);
        const updatedSource = enrichSource(
            shouldRederiveDisplayName
                ? { ...merged, displayName: undefined }
                : merged,
        );
        const newSourcesMap = new Map(sourcesMap);
        newSourcesMap.set(sourceId, updatedSource);

        set({
            sources: Array.from(newSourcesMap.values()),
            sourcesMap: newSourcesMap,
        });
    },
    removeSource: (sourceId) => {
        const { sources } = get();
        const newSources = sources.filter((s) => s.id !== sourceId);
        set({
            sources: newSources,
            sourcesMap: createSourcesMap(newSources),
        });
    },
    getSourceById: (id) => {
        return get().sourcesMap.get(id);
    },
    selectSource: (id) => {
        set((state) => {
            const newSelected = new Set(state.selected);
            newSelected.add(id);
            return { selected: newSelected, all: false };
        });
    },
    deselectSource: (id) => {
        set((state) => {
            const newSelected = new Set(state.selected);
            newSelected.delete(id);
            return {
                selected: newSelected,
                selectedFolders: pruneFoldersContaining(state.selectedFolders, [
                    id,
                ]),
                all: false,
            };
        });
    },
    toggleSource: (id) =>
        set((state) => {
            const next = new Set(state.selected);
            const removing = next.has(id);
            removing ? next.delete(id) : next.add(id);
            return {
                selected: next,
                selectedFolders: removing
                    ? pruneFoldersContaining(state.selectedFolders, [id])
                    : state.selectedFolders,
                all: false,
            };
        }),
    selectAll: () => {
        set({
            all: true,
            selected: new Set(),
            selectedFolders: new Map(),
            excluded: new Set(),
        });
    },
    clearSelection: () => {
        set({ all: false, selected: new Set(), selectedFolders: new Map() });
    },
    setSelectedSources: (sourceIds, folders) => {
        set({
            all: false,
            selected: sourceIds,
            selectedFolders: folders ?? new Map(),
            excluded: new Set(),
        });
    },
    getSelectedSourceIds: () => {
        const { selected } = get();
        return Array.from(selected);
    },
}));

export const selectCameras = (state: SourcesState): FullSource[] =>
    state.sources.filter((s) => s.type === SourceType.CAMERA);

export const selectVideos = (state: SourcesState): FullSource[] =>
    state.sources.filter((s) => s.type === SourceType.FILE);
